import React, { useState } from "react";
import {
  Button,
  Chip,
  FormFieldSet,
  useFormHandler,
  Modal,
  showToast,
} from "../../../../../../components";
import { fetchTemplateById } from "../../../../../../redux/slices/compaigns/compaignsApi";
import { useLoginUser } from "../../../../../../hooks/useLoginUser";
import { useDispatch } from "react-redux";
import { fetchEmailLogsApi } from "../../../../../../redux/slices/emaillogs/emaillogsApi";

const SendEmailModal = ({
  isOpen,
  onClose,
  selectedContacts = [],
  fromEmail = "",
  userData,
  jobId,
  companyId,
}) => {
  
  const dispatch = useDispatch();
  const [removedIds, setRemovedIds] = useState([]);
  const [fromRemoved, setFromRemoved] = useState(false);
  const [manualEmails, setManualEmails] = useState([]);
  const [ccManualEmails, setCcManualEmails] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const visibleContacts = selectedContacts.filter(
    (c) => !removedIds.includes(c.id),
  );
  const removedContacts = selectedContacts.filter((c) =>
    removedIds.includes(c.id),
  );
  // Combined recipient list
  const allToEmails = [...visibleContacts.map((c) => c.email), ...manualEmails];
  const allCcEmails = [...ccManualEmails];
  const initialData = {
    emailSource: "",
    emailAccount: "",
    template: "",
    subject: "",
    body: "",
  };

  const { formData, errors, handleChange, validateAllFields, setFormData } =
    useFormHandler(initialData, {
      emailSource: { type: "select", required: true },
      template: { type: "select", required: true },
      subject: { required: true },
    });

  const canSend =
    allToEmails.length > 0 &&
    !fromRemoved &&
    formData.emailSource &&
    formData.template &&
    formData.subject?.trim();

  const handleClose = () => {
    setFormData(initialData);
    setRemovedIds([]);
    setFromRemoved(false);
    setManualEmails([]);
    setCcManualEmails([]);
    onClose();
  };

  const removeContact = (id) => setRemovedIds((prev) => [...prev, id]);
  const restoreContact = (id) =>
    setRemovedIds((prev) => prev.filter((rid) => rid !== id));

  const handleDragStart = (e, type, value, id = null) => {
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({ type, value, id }),
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDropToCc = (e) => {
    e.preventDefault();
    const dataString = e.dataTransfer.getData("application/json");
    if (!dataString) return;

    try {
      const { type, value, id } = JSON.parse(dataString);

      // Add to CC if not already there
      setCcManualEmails((prev) => {
        if (prev.includes(value)) return prev;
        return [...prev, value];
      });

      // Remove from To
      if (type === "contact" && id) {
        removeContact(id);
      } else if (type === "manual") {
        setManualEmails((prev) => prev.filter((email) => email !== value));
      }
    } catch (error) {
      console.error("Drop failed", error);
    }
  };

  const handleDropToTo = (e) => {
    e.preventDefault();
    const dataString = e.dataTransfer.getData("application/json");
    if (!dataString) return;

    try {
      const { type, value } = JSON.parse(dataString);

      if (type === "cc") {
        // Add to To (Manual) if not already there
        setManualEmails((prev) => {
          if (prev.includes(value)) return prev;
          return [...prev, value];
        });
        // Remove from CC
        setCcManualEmails((prev) => prev.filter((email) => email !== value));
      }
    } catch (error) {
      console.error("Drop failed", error);
    }
  };

  const handleGetTemplate = async (templateId) => {
    try {
      const response = await fetchTemplateById({
        coll: "Template",
        fil: { uiid: { type: "NONE", value: templateId } },
        ret: "body subject uiid",
      });
      if (response?.data) {
        setFormData((prev) => ({
          ...prev,
          body: response.data.body || "",
          subject: response.data.subject || "",
        }));
      }
    } catch (error) {
      console.error("Failed to fetch template", error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Send Email"
      titleColor="primary"
      width="max-w-4xl"
      showCloseButton
      bodyClassName="flex flex-col overflow-auto"
    >
      {/* Email Config Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50/60">
        <FormFieldSet
          type="select"
          title="Email Source"
          placeholder="Select source"
          size="sm"
          shadow
          options={[
            { label: "Gmail", value: 1 },
            { label: "Outlook", value: 2 },
            { label: "Mailgun", value: 3 },
            { label: "AWS-SES", value: 4 },
          ]}
          value={formData.emailSource}
          onChange={(value) => {
            handleChange("emailSource", value);
          }}
          error={errors.emailSource}
        />
        <FormFieldSet
          type="emailSourceAccounts"
          placeholder="Select Account"
          title="Email Account"
          size="sm"
          // options={[
          //   { label: "brown@tektreeinc.com", value: "1" },
          //   { label: "aden@tektreeinc.com", value: "2" },
          // ]}
          value={formData.emailAccount}
          onChange={(value) => handleChange("emailAccount", value)}
          error={errors.emailAccount}
          disabled={!formData.emailSource}
          filters={{ category: 11, type: formData.emailSource }}
          // filters={{ employeeId: employeeProfileUiid }}
          shadow={true}
        />

        <FormFieldSet
          type="templates"
          title="Template"
          placeholder="Select template"
          size="sm"
          showSearch
          shadow
          value={formData.template}
          onChange={(value) => {
            handleChange("template", value);
            if (value?.value) handleGetTemplate(value.value);
          }}
          error={errors.template}
        />
      </div>

      {/* From Emails Field  */}
      <div className="flex items-start gap-3 px-4 py-3 border-b border-gray-100">
        <span className="text-xs text-gray-400 pt-[5px] w-12 shrink-0">
          From
        </span>
        <div className="flex flex-col gap-1.5 flex-1 min-w-0">
          {fromRemoved ? (
            <span className="text-sm text-gray-400 italic">
              No sender — restore from below
            </span>
          ) : (
            <div className="flex items-center gap-2">
              <Chip
                label={formData.emailAccount?.label || fromEmail}
                color="info"
                variant="contained"
                size="sm"
                icon="person"
                iconPosition="before"
                onDelete={() => setFromRemoved(true)}
              />
            </div>
          )}

          {/* Restore row */}
          {fromRemoved && (
            <div className="flex items-center gap-1.5">
              <Chip
                label={formData.emailAccount?.label || fromEmail}
                color="black"
                variant="outlined"
                size="sm"
                icon="undo"
                iconPosition="after"
                className="opacity-50 hover:opacity-100 cursor-pointer transition-opacity line-through"
                onClick={() => setFromRemoved(false)}
              />
              <span className="text-[10px] text-gray-400">
                click to restore
              </span>
            </div>
          )}
        </div>
      </div>

      {/* To Email Field*/}
      {allToEmails.length > 0 && (
        <div
          className="flex items-start gap-3 px-4 pt-3 pb-1"
          onDragOver={handleDragOver}
          onDrop={handleDropToTo}
        >
          <span className="text-xs text-gray-400 pt-[5px] w-12 shrink-0">
            To
          </span>
          <div className="flex flex-wrap gap-1.5 flex-1">
            {/* Chips from selected table rows */}
            {visibleContacts.map((contact) => (
              <div
                key={contact.id}
                draggable
                onDragStart={(e) =>
                  handleDragStart(e, "contact", contact.email, contact.id)
                }
                className="cursor-grab active:cursor-grabbing"
              >
                <Chip
                  label={contact.email}
                  color="info"
                  variant="contained"
                  size="sm"
                  icon="person"
                  onDelete={() => removeContact(contact.id)}
                />
              </div>
            ))}

            {/* Chips from manually typed emails */}
            {manualEmails.map((email, idx) => (
              <div
                key={`manual-${idx}`}
                draggable
                onDragStart={(e) => handleDragStart(e, "manual", email)}
                className="cursor-grab active:cursor-grabbing"
              >
                <Chip
                  label={email}
                  color="leave"
                  variant="contained"
                  size="sm"
                  onDelete={() =>
                    setManualEmails((prev) => prev.filter((_, i) => i !== idx))
                  }
                />
              </div>
            ))}

            <span className="text-[10px] text-gray-400 self-center">
              {allToEmails.length} recipient
              {allToEmails.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      )}

      {/* Manual email input (Enter to add, paste supported) */}
      <div className="flex items-start gap-3 px-4 pb-3 border-b border-gray-100">
        {/* Spacer: shows "To" label only when no chips are present yet */}
        <span className="text-xs text-gray-400 pt-[18px] w-12 shrink-0">
          {allToEmails.length === 0 ? "To" : ""}
        </span>
        <div
          className="flex-1 pt-1"
          onDragOver={handleDragOver}
          onDrop={handleDropToTo}
        >
          <FormFieldSet
            key={`to-email-${manualEmails.length}`}
            type="email"
            multiple
            noTitle
            autocomplete={"email"}
            placeholder="Type the email and press Enter to add more emails…"
            size="sm"
            shadow
            value={manualEmails}
            onChange={(updatedList) => setManualEmails(updatedList)}
          />
        </div>
      </div>

      {/* CC Email Field*/}
      {allCcEmails.length > 0 && (
        <div
          className="flex items-start gap-3 px-4 pt-3 pb-1"
          onDragOver={handleDragOver}
          onDrop={handleDropToCc}
        >
          <span className="text-xs text-gray-400 pt-[5px] w-12 shrink-0">
            CC
          </span>
          <div className="flex flex-wrap gap-1.5 flex-1">
            {ccManualEmails.map((email, idx) => (
              <div
                key={`cc-manual-${idx}`}
                draggable
                onDragStart={(e) => handleDragStart(e, "cc", email)}
                className="cursor-grab active:cursor-grabbing"
              >
                <Chip
                  label={email}
                  color="leave"
                  variant="contained"
                  size="sm"
                  onDelete={() =>
                    setCcManualEmails((prev) =>
                      prev.filter((_, i) => i !== idx),
                    )
                  }
                />
              </div>
            ))}

            <span className="text-[10px] text-gray-400 self-center">
              {allCcEmails.length} recipient
              {allCcEmails.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      )}

      {/* Manual CC email input */}
      <div className="flex items-start gap-3 px-4 pb-3 border-b border-gray-100">
        <span className="text-xs text-gray-400 pt-[18px] w-12 shrink-0">
          {allCcEmails.length === 0 ? "CC" : ""}
        </span>
        <div
          className="flex-1 pt-1"
          onDragOver={allCcEmails.length === 0 ? handleDragOver : undefined}
          onDrop={allCcEmails.length === 0 ? handleDropToCc : undefined}
        >
          <FormFieldSet
            key={`cc-email-${ccManualEmails.length}`}
            type="email"
            multiple
            noTitle
            autocomplete={"work email"}
            placeholder="Type the email and press Enter to add more emails…"
            size="sm"
            shadow
            value={ccManualEmails}
            onChange={(updatedList) => setCcManualEmails(updatedList)}
          />
        </div>
      </div>

      {/* Removed contacts restore row */}
      {/* {removedContacts.length > 0 && (
        <div className="flex items-start gap-3 px-4 py-2.5 border-b border-dashed border-gray-200 bg-gray-50">
          <span className="text-[10px] text-gray-400 pt-[5px] w-12 shrink-0 uppercase tracking-wide leading-tight">
            Removed
          </span>
          <div className="flex flex-wrap gap-1.5 flex-1">
            {removedContacts.map((contact) => (
              <Chip
                key={contact.id}
                label={contact.email}
                color="black"
                variant="outlined"
                size="sm"
                icon="undo"
                iconPosition="after"
                className="opacity-50 hover:opacity-100 cursor-pointer transition-opacity line-through"
                onClick={() => restoreContact(contact.id)}
              />
            ))}
          </div>
        </div>
      )} */}

      {/* Subject */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-100">
        <span className="text-xs text-gray-400 w-12 shrink-0">Subject</span>
        <input
          type="text"
          placeholder="Auto-filled from template or type here…"
          value={formData.subject}
          onChange={(e) => handleChange("subject", e.target.value)}
          className="flex-1 text-sm text-gray-800 bg-transparent outline-none placeholder:text-gray-300 py-1"
        />
      </div>

      {/* Body */}
      <div className="px-4 pt-3 pb-2">
        <FormFieldSet
          type="textArea"
          title=""
          placeholder="Auto-filled from template or write here…"
          value={formData.body}
          onChange={(value) => handleChange("body", value)}
          error={errors.body}
          rows={7}
          shadow
        />
      </div>
      <div className="flex items-center justify-end px-4 py-3 border-t border-gray-100 bg-gray-50/60 mt-auto">
        <div className="flex items-center gap-2">
          <Button
            title="Cancel"
            variant="outlined"
            size="sm"
            onClick={handleClose}
          />
          <Button
            title={isSending ? "Sending..." : "Send"}
            icon="send"
            size="sm"
            disabled={!canSend || isSending}
            onClick={async () => {
              if (!validateAllFields()) return;

              setIsSending(true);
              try {
                await fetchEmailLogsApi({
                  coll: "EmailLog",
                  payload: {
                    from: formData.emailAccount?.label || fromEmail,
                    to: allToEmails,
                    cc: allCcEmails,
                    addedBy: userData?.uiid,
                    templateId: formData.template?.value,
                    // source: 7,
                    source: formData.emailSource,
                    integrationAccessId: formData?.emailAccount?.accessUiid,
                    jobId,
                    companyId,
                    contactId: selectedContacts.map((contact) => contact.id),
                  },
                });
                showToast("Email sent successfully", "success");
              } catch (error) {
                showToast(
                  error?.message || "Failed to send email. Please try again.","error"
                ); 
              } finally {
                handleClose();
                setIsSending(false);
              }
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SendEmailModal;
